import { computed, Signal } from '@angular/core';
import { ErrorDetails, ExecuteDataQueryElementUsage, SortDirection } from 'src/app/services/api.services';
import { DataAggregation, DataModel } from '../../dataModel';
import { DataType } from '../../dataModel/DataType';
import { VisualField } from '../VisualField';
import { VisualFieldTarget } from '../VisualFieldTarget';
import { VisualDataRequest } from './VisualDataRequest';
import { VisualDataAdapter } from './VisualDataAdapter';
import { Observable } from 'rxjs';
import { isNullOrUndefined } from 'src/app/shared/utils';

export abstract class VisualDataSource {
  /** Gets a query that can be used to access data as per the given request. */
  query(request: VisualDataRequest, restart?: boolean): VisualDataQuery {
    return this.queryImpl(request, restart);
  }

  protected abstract queryImpl(request: VisualDataRequest, restart?: boolean): VisualDataQuery;
}

export interface VisualDataQuery {
  readonly request: VisualDataRequest;

  readonly resources: VisualDataQueryResourceManager;

  readonly status: Signal<VisualDataQueryStatus>;
  readonly id: Signal<string | undefined>;

  readonly requestedAt: Signal<Date>;

  readonly executingAt: Signal<Date | undefined>;
  readonly executing: Signal<boolean>;

  readonly completedAt: Signal<Date | undefined>;
  readonly completed: Signal<boolean>;

  /** Whether the query has completed successfully. */
  readonly success: Signal<boolean>;

  /** Whether the query has completed with a failure. */
  readonly failure: Signal<boolean>;
  
  readonly error: Signal<ErrorDetails | undefined>;
  
  /** 
   * First data table for this query.
   * This is equvialent to `tables()[0]`. */
  readonly data: Signal<VisualDataQueryTable | undefined>;

  /** Collection of all data tables for this query. */
  readonly tables: Signal<ReadonlyArray<VisualDataQueryTable> | undefined>;


  /** Whether the query has been aborted. */
  readonly aborted: Signal<boolean>;

  /**
   * Aborts any asynchronous operations associated with this query,
   * including operations started for its resources. */
  abort(): void;
}

export interface VisualDataQueryResourceManager {
  readonly anyLoading: () => boolean;

  abortAll(): void;
}

export enum VisualDataQueryStatus {
  /**
   * Indicates that the query is has been requested, and is waiting to be executed.
   * `data()` and `tables()` will return undefined. */
  Requested,

  /**
   * Indicates that the query is currently executing.
   * `data()` and `tables()` will return undefined. */
  Executing,

  /**
   * Indicates that the query execution has succeeded. 
   * `data()` and `tables()` will return values. */
  Success,

  /**
   * Indicates that the query execution has failed.
   * `data()` and `tables()` will return undefined.
   * `error()` will return details about the failure. */
  Failure,
}


//#region VisualDataQueryTable
export interface VisualDataQueryTable {
  readonly request: VisualDataRequest;
  readonly adapter: VisualDataAdapter;

  /**
   * Collection of columns present in this table.
   * This collection can be used to map visual fields to indices in rows. */
  readonly columns: VisualDataQueryColumnCollection;


  /**
   * Total number of rows in this table. */
  readonly totalRows: number;

  /**
   * When `true`, this means the query resulted in too many rows. */
  readonly exceededRowLimits: boolean;


  /**
   * Number of rows per chunk (note the last chunk may have fewer rows). */
  readonly rowsPerChunk: number;

  /** 
   * Number of chunks in this table. */
  readonly chunks: number;


  /** 
   * Gets a request to a chunk of this table.
   * If `options.order` is provided, the chunks will be ordered.
   * The index is relative to the ordered chunks. */
  chunk(chunkIndex: number, options?: VisualDataTableResourceOptions): VisualDataResource<ChunkResult>;

  /**
   * Gets a request to all chunks of this table. */
  rows(options?: VisualDataTableResourceOptions): VisualDataResource<RowsResult>;
}

export interface ColumnOrder {
  readonly columnIndex: number;
  readonly sort: SortDirection;
}

export interface VisualDataTableResourceOptions extends VisualDataResourceOptions {
  /** Order to apply to the table, before returning values. */
  readonly order?: ReadonlyArray<ColumnOrder>;

  /** 
   * Number of rows to skip.
   * This is relative to the first row of the query (e.g. first row of a chunk).
   **/
  readonly skip?: number;
  
  /** 
   * Maximum number of rows to return.
   **/
  readonly take?: number;

  /**
   * Signal for this resource to abort (in addition to query cancellation). 
   **/
  readonly abortSignal?: Observable<void>;
}

export type Values = ReadonlyArray<any>;
export type Rows = ReadonlyArray<Values>

export interface RowsResult {
  /**
   * Collection of columns included in this result.
   * This is not the same as the collection of all columns of a query.
   * The order of these columns follows data order in the rows (i.e. sorted by `column.dataIndex`). */
  readonly columns: VisualDataQueryColumnCollection;

  /**
   * Array of arrays, where each inner array represents a row. 
   * To map visual fields to relevant indices in the array, use `columns`.
   **/
  readonly rows: Rows;
}

export interface ChunkResult extends RowsResult {
  readonly index: number;

  /** Whether this chunk is the last one. */
  readonly last: boolean;

  /** Offset of the first row in this chunk. */
  readonly rowOffset: number;
}
//#endregion

//#region VisualDataQueryColumn
export interface VisualDataQueryColumn {
  /** Index of this column in the {@see VisualDataQueryDataColumnCollection} it belongs to. */
  readonly index: number;

  /** 
   * Visual field target this column maps to. 
   * May be undefined, if not applicable. */
  readonly target?: VisualFieldTarget;

  /** Data type of the values in a row of data for this column. */
  readonly dataType: DataType;

  /** Index into a row of data where value of this column is present. */
  readonly dataIndex: number;

  /** Name of this column. **/
  readonly name: string;

  /** Identifier of this column that can be used to reference this column in expressions. */
  readonly identifier: string;

  /** Usage of this column in the query. */
  readonly usage: ExecuteDataQueryElementUsage;

  /** Name of the column this column has been defined for. **/
  readonly baseElement: string;
}

export class VisualDataQueryColumnCollection<T extends VisualDataQueryColumn = VisualDataQueryColumn> extends Array<T> {
  constructor(...columns: T[]) {
    super(...columns);
  }
  
  /**
   * Returns a {@see VisualDataQueryDataColumn} that maps to the specified visual field target.
   * If no such column is found, returns `undefined`.
   * 
   * It is guaranteed that there will be only one column for an unqiue target.
   * A target's uniqueness is defined by it's data field and aggregation.
   **/
  tryGet(fieldOrTarget: VisualField | VisualFieldTarget, forSorting?: boolean): T | undefined {
    let target =
      (fieldOrTarget instanceof VisualField)
        ? fieldOrTarget.target
        : fieldOrTarget;

    if (forSorting) {
      let c = this.find(x => x.usage === ExecuteDataQueryElementUsage.Sorting && target.equals(x.target));
      if (c)
        return c;
    }

    return this.find(x => target.equals(x.target));
  }
  
  /**
   * Returns a {@see VisualDataQueryDataColumn} that maps to the specified visual field target.
   * If no such column is found, throws an error.
   * 
   * It is guaranteed that there will be only one column for an unqiue target.
   * A target's uniqueness is defined by it's data field and aggregation.
   **/
  get(fieldOrTarget: VisualField | VisualFieldTarget, forSorting?: boolean): T {
    let column = this.tryGet(fieldOrTarget, forSorting);
    if (!column) {
      throw new Error(
        `Could not find column for the target '${fieldOrTarget.referenceString()}'`,
        { cause: fieldOrTarget });
    }

    return column;
  }
}
//#endregion

//#region VisualDataResource
export interface VisualDataResourceOptions {
  readonly debugName?: string;
}

export interface VisualDataResource<T> {
  readonly table: VisualDataQueryTable;
  readonly status: Signal<VisualDataResourceStatus>;

  readonly loadingAt: Signal<Date | undefined>;
  readonly loading: Signal<boolean>;

  readonly completedAt: Signal<Date | undefined>;
  readonly completed: Signal<boolean>;

  /** Whether the resource has completed successfully. */
  readonly success: Signal<boolean>;

  /** Whether the resource has completed with a failure. */
  readonly failure: Signal<boolean>;

  readonly error: Signal<ErrorDetails | undefined>;


  /** 
   * Follows successful completion, and indicates the result is available.
   * `false` when not completed successfully. */
  readonly hasResult: Signal<boolean>;

  readonly result: Signal<T | undefined>;


  /**
   * Aborts any asynchronous operations associated with this resource.
   * This method will set this resource's status as failed (regardless of its current status). */
  abort();
}

export namespace VisualDataResource {
  export function adapt<T, R>(
    source: VisualDataResource<T>,
    transform: (value: T) => R,
    abortSource?: boolean): VisualDataResource<R> {
    return new Adapter<T, R>(source, transform, abortSource);
  }

  export class Adapter<T, R> implements VisualDataResource<R> {
    constructor(
      public readonly source: VisualDataResource<T>,
      public readonly transform: (value: T) => R,
      public readonly abortSource?: boolean) { }

    get table() { return this.source.table; }
    get status() { return this.source.status; }

    get loadingAt() { return this.source.loadingAt; }
    get loading() { return this.source.loading; }

    get completedAt() { return this.source.completedAt; }
    get completed() { return this.source.completed; }

    get success() { return this.source.success; }

    get failure() { return this.source.failure; }
    get error() { return this.source.error; }


    get hasResult() { return this.source.hasResult; }

    readonly result = computed<R | null>(() => {
      if (this.hasResult()) {
        return this.transform(this.source.result());
      }

      return null;
    });

    abort() {
      if (this.abortSource)
        this.source.abort();
    }
  }
}

export enum VisualDataResourceStatus {
  /**
   * Indicates that the request is currently pending (i.e. waiting for its query to complete).
   * `result()` will return `undefined`. */
  Pending,

  /**
   * Indicates that the request is currently loading.
   * `result()` will return `undefined`. */
  Loading,

  /**
   * Indicates that the request has succeeded.
   * `result()` will return a value. */
  Success,

  /**
   * Indicates that the request has failed.
   * `result()` will return `undefined`. */
  Failure,

  /** 
   * Indicates that the request has expired. 
   * The request should be reloaded if its result is needed.
   * `result()` will return the previous result if available, until reloaded. */
  Expired
}
